import { TimelineLayout } from "@/components/ui/timeline/timeline-layout";
import type { TimelineElement } from "@/components/ui/timeline/timeline.type";
import { ProjectRevisionType } from "@employee-management/common";
import {
  CheckCircle,
  Clock as ClockIcon,
  History,
  Plus,
  Edit,
  Activity,
} from "lucide-react";
import type { ProjectRevisionHistory } from "@/types/project.types";

interface OvertimeApprovalHistoryProps {
  history: ProjectRevisionHistory[];
}

export function OvertimeApprovalHistory({
  history,
}: OvertimeApprovalHistoryProps) {
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No approval history available</p>
      </div>
    );
  }

  const getRevisionIcon = (revisionType: string) => {
    switch (revisionType) {
      case ProjectRevisionType.PROJECT_CREATED:
        return <Plus className="h-4 w-4" />;
      case ProjectRevisionType.PROJECT_UPDATED:
        return <Edit className="h-4 w-4" />;
      case ProjectRevisionType.PROJECT_STATUS_CHANGED:
        return <Activity className="h-4 w-4" />;
      case ProjectRevisionType.OVERTIME_ADDED:
        return <ClockIcon className="h-4 w-4" />;
      case ProjectRevisionType.OVERTIME_UPDATED:
        return <Edit className="h-4 w-4" />;
      case ProjectRevisionType.OVERTIME_STATUS_CHANGED:
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <History className="h-4 w-4" />;
    }
  };

  const getRevisionColor = (revisionType: string): TimelineElement["color"] => {
    switch (revisionType) {
      case ProjectRevisionType.PROJECT_CREATED:
        return "primary";
      case ProjectRevisionType.PROJECT_STATUS_CHANGED:
        return "accent";
      case ProjectRevisionType.OVERTIME_ADDED:
        return "primary";
      case ProjectRevisionType.OVERTIME_PAYMENT_STATUS_CHANGED:
      case ProjectRevisionType.OVERTIME_STATUS_CHANGED:
        return "primary";
      case ProjectRevisionType.PROJECT_UPDATED:
      case ProjectRevisionType.OVERTIME_UPDATED:
        return "muted";

      default:
        return "muted";
    }
  };

  const getRevisionTitle = (item: ProjectRevisionHistory) => {
    const userName = item.changedByRef.name;

    switch (item.revisionType) {
      case ProjectRevisionType.PROJECT_CREATED:
        return `${userName} - Project Created`;
      case ProjectRevisionType.PROJECT_UPDATED:
        return `${userName} - Project Updated`;
      case ProjectRevisionType.PROJECT_STATUS_CHANGED:
        return `${userName} - Status Changed`;
      case ProjectRevisionType.OVERTIME_ADDED:
        return `${userName} - Overtime Added`;
      case ProjectRevisionType.OVERTIME_UPDATED:
        return `${userName} - Overtime Updated`;
      case ProjectRevisionType.OVERTIME_STATUS_CHANGED:
        return `${userName} - Overtime Status Changed`;
      case ProjectRevisionType.OVERTIME_PAYMENT_STATUS_CHANGED:
        return `${userName} - Overtime Payment Status Changed`;
      default:
        return `${userName} - ${item.revisionType}`;
    }
  };

  const getActionDescription = (item: ProjectRevisionHistory) => {
    // Parse changeDetails to get status information
    if (item.changeDetails) {
      try {
        const details = JSON.parse(item.changeDetails);
        if (details.oldStatus && details.newStatus) {
          return `Changed from ${details.oldStatus} to ${details.newStatus}`;
        }
      } catch (e) {
        // Fallback to changeDescription if parsing fails
      }
    }
    return item.changeDescription;
  };

  const getCommentFromHistory = (item: ProjectRevisionHistory) => {
    if (item.changeDetails) {
      try {
        const details = JSON.parse(item.changeDetails);
        return details.comment;
      } catch (e) {
        // Fallback
      }
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Transform history data into TimelineElement format
  const timelineItems: TimelineElement[] = history
    .sort((a, b) => {
      return new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime();
    })
    .map((item, index) => {
      const comment = getCommentFromHistory(item);

      return {
        id: index,
        date: formatDate(item.changedAt),
        title: getRevisionTitle(item),
        description: comment ? (
          <div>
            <p>{getActionDescription(item)}</p>
            <p className="text-muted-foreground">
              <span className="font-bold">Message:</span> {comment}
            </p>
          </div>
        ) : (
          getActionDescription(item)
        ),
        icon: getRevisionIcon(item.revisionType),
        color: getRevisionColor(item.revisionType),
        status: "completed" as const,
      };
    });

  return (
    <TimelineLayout
      items={timelineItems}
      size="md"
      animate={true}
      className="max-w-none"
    />
  );
}
